/**
 * NSI Car Database (NSICD) - Shopify Theme JavaScript
 * Year/Make/Model Vehicle Fitment Selector & Garage Manager
 */

(function() {
  'use strict';

  // Configuration
  const NSICD_CONFIG = {
    apiBase: 'https://audiowattstore.com/NSICD/api/ymm.php',
    storageKeys: {
      garage: 'nsicd_garage',
      activeVehicle: 'nsicd_active_vehicle',
      garageCount: 'nsicd_garage_count'
    }
  };

  // ============================================================
  // YMM Selector Web Component
  // ============================================================

  class NsicdSelector extends HTMLElement {
    constructor() {
      super();
      this.customerId = null;
      this.selectedYear = null;
      this.selectedMake = null;
      this.selectedModel = null;
      this.selectedYearId = null;
      this.modelsData = []; // Store full model data for style filtering
    }

    connectedCallback() {
      this.init();
    }

    init() {
      // Get DOM elements
      this.yearSelect = this.querySelector('[data-nsicd-year]');
      this.makeSelect = this.querySelector('[data-nsicd-make]');
      this.modelSelect = this.querySelector('[data-nsicd-model]');
      this.styleSelect = this.querySelector('[data-nsicd-style]');
      this.searchBtn = this.querySelector('[data-nsicd-search]');
      this.loadingIndicator = this.querySelector('[data-nsicd-loading]');

      // Get customer ID from meta tag
      const customerMeta = document.querySelector('meta[name="shopify-customer-id"]');
      if (customerMeta && customerMeta.content) {
        this.customerId = customerMeta.content;
      }

      // Load initial years
      this.loadYears();

      // Attach event listeners
      if (this.yearSelect) {
        this.yearSelect.addEventListener('change', (e) => this.onYearChange(e));
      }
      if (this.makeSelect) {
        this.makeSelect.addEventListener('change', (e) => this.onMakeChange(e));
      }
      if (this.modelSelect) {
        this.modelSelect.addEventListener('change', (e) => this.onModelChange(e));
      }
      if (this.styleSelect) {
        this.styleSelect.addEventListener('change', (e) => this.onStyleChange(e));
      }
      if (this.searchBtn) {
        this.searchBtn.addEventListener('click', () => this.goToResults());
      }
    }

    showLoading(show) {
      if (this.loadingIndicator) {
        this.loadingIndicator.style.display = show ? 'block' : 'none';
      }
    }

    async fetchApi(action, params = {}) {
      const url = new URL(NSICD_CONFIG.apiBase);
      url.searchParams.set('action', action);
      Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          url.searchParams.set(key, value);
        }
      });

      try {
        const response = await fetch(url.toString(), {
          credentials: 'include'
        });
        return await response.json();
      } catch (error) {
        console.error('NSICD API Error:', error);
        return { success: false, error: error.message };
      }
    }

    async loadYears() {
      this.showLoading(true);
      const data = await this.fetchApi('get_years');
      this.showLoading(false);

      if (data.success && this.yearSelect) {
        this.populateSelect(this.yearSelect, data.years.map(y => ({ value: y, label: y })), 'Select Year');
      }
    }

    async onYearChange(e) {
      this.selectedYear = e.target.value;
      this.resetSelect(this.makeSelect, 'Select Make');
      this.resetSelect(this.modelSelect, 'Select Model');
      this.resetSelect(this.styleSelect, 'Select Style');
      this.modelsData = [];
      this.updateSearchButton();

      if (!this.selectedYear) return;

      this.showLoading(true);
      const data = await this.fetchApi('get_makes', { year: this.selectedYear });
      this.showLoading(false);

      if (data.success && this.makeSelect) {
        this.populateSelect(
          this.makeSelect,
          data.makes.map(m => ({ value: m.make_id, label: m.make_name })),
          'Select Make'
        );
      }
    }

    async onMakeChange(e) {
      this.selectedMake = e.target.value;
      this.resetSelect(this.modelSelect, 'Select Model');
      this.resetSelect(this.styleSelect, 'Select Style');
      this.modelsData = [];
      this.updateSearchButton();

      if (!this.selectedMake) return;

      this.showLoading(true);
      const data = await this.fetchApi('get_models', {
        year: this.selectedYear,
        make_id: this.selectedMake
      });
      this.showLoading(false);

      if (data.success && this.modelSelect) {
        // Store full data for style filtering
        this.modelsData = data.models;

        // Get unique model names
        const uniqueModels = [...new Map(data.models.map(m => [m.model_name, m])).values()];

        this.populateSelect(
          this.modelSelect,
          uniqueModels.map(m => ({ value: m.model_name, label: m.model_name })),
          'Select Model'
        );
      }
    }

    onModelChange(e) {
      this.selectedModel = e.target.value;
      this.resetSelect(this.styleSelect, 'Select Style');
      this.selectedYearId = null;
      this.updateSearchButton();

      if (!this.selectedModel) return;

      // Filter styles for selected model
      const stylesForModel = this.modelsData.filter(m => m.model_name === this.selectedModel);

      if (stylesForModel.length === 1 && !stylesForModel[0].style) {
        // Only one option with no style - auto-select it
        this.selectedYearId = stylesForModel[0].year_id;
        if (this.styleSelect) {
          this.styleSelect.innerHTML = '<option value="">N/A</option>';
          this.styleSelect.disabled = true;
        }
        this.updateSearchButton();
      } else if (stylesForModel.length === 1) {
        // Only one style - auto-select it
        this.selectedYearId = stylesForModel[0].year_id;
        if (this.styleSelect) {
          this.populateSelect(
            this.styleSelect,
            [{ value: stylesForModel[0].year_id, label: stylesForModel[0].style || 'Standard' }],
            'Select Style'
          );
          this.styleSelect.value = stylesForModel[0].year_id;
        }
        this.updateSearchButton();
      } else {
        // Multiple styles - populate dropdown
        if (this.styleSelect) {
          this.populateSelect(
            this.styleSelect,
            stylesForModel.map(m => ({
              value: m.year_id,
              label: m.style || 'Standard'
            })),
            'Select Style'
          );
        }
      }
    }

    onStyleChange(e) {
      this.selectedYearId = e.target.value;
      this.updateSearchButton();
    }

    populateSelect(select, options, placeholder) {
      select.innerHTML = `<option value="">${placeholder}</option>`;
      options.forEach(opt => {
        const option = document.createElement('option');
        option.value = opt.value;
        option.textContent = opt.label;
        select.appendChild(option);
      });
      select.disabled = false;
    }

    resetSelect(select, placeholder) {
      if (select) {
        select.innerHTML = `<option value="">${placeholder}</option>`;
        select.disabled = true;
      }
    }

    updateSearchButton() {
      if (this.searchBtn) {
        this.searchBtn.disabled = !this.selectedYearId;
      }
    }

    goToResults() {
      if (!this.selectedYearId) return;

      const year = this.yearSelect.value;
      const makeName = this.makeSelect.options[this.makeSelect.selectedIndex].text;
      const modelName = this.modelSelect.options[this.modelSelect.selectedIndex].text;
      const styleName = this.styleSelect && this.styleSelect.value
        ? this.styleSelect.options[this.styleSelect.selectedIndex].text
        : '';

      // Build URL - go to vehicle search page with all params (like AudioWatt)
      const urlParams = [
        `year_id/${this.selectedYearId}`,
        `Year/${year}`,
        `Make/${encodeURIComponent(makeName)}`,
        `Model/${encodeURIComponent(modelName)}`
      ];
      if (styleName && styleName !== 'N/A' && styleName !== 'Select Style') {
        urlParams.push(`Style/${encodeURIComponent(styleName)}`);
      }

      window.location.href = `/pages/vehicle-results?${urlParams.join('/')}`;
    }
  }

  // Register the custom element
  if (!customElements.get('nsicd-selector')) {
    customElements.define('nsicd-selector', NsicdSelector);
  }

  // ============================================================
  // YMM Results Component
  // ============================================================

  class NsicdResults extends HTMLElement {
    constructor() {
      super();
      this.vehicle = null;
      this.fitments = [];
      this.customerId = null;
    }

    connectedCallback() {
      this.init();
    }

    async init() {
      // Get customer ID
      const customerMeta = document.querySelector('meta[name="shopify-customer-id"]');
      if (customerMeta && customerMeta.content) {
        this.customerId = customerMeta.content;
      }

      // Get year_id from URL or data attribute
      // URL format: ?year_id/5988/Year/2024/Make/Acura/Model/Integra
      let yearId = this.dataset.yearId;
      const queryString = window.location.search.substring(1); // Remove the leading '?'

      if (queryString) {
        // Parse slash-separated format: year_id/5988/Year/2024/...
        const parts = queryString.split('/');
        for (let i = 0; i < parts.length - 1; i += 2) {
          if (parts[i] === 'year_id' && parts[i + 1]) {
            yearId = parts[i + 1];
            break;
          }
        }
      }

      if (yearId) {
        await this.loadFitments(yearId);
      }

      // Attach event listeners
      this.addEventListener('click', (e) => {
        const saveBtn = e.target.closest('[data-save-to-garage]');
        if (saveBtn) {
          // If already in garage, open the drawer instead
          if (saveBtn.dataset.inGarage === 'true') {
            if (window.nsicdGarage && typeof window.nsicdGarage.toggleDrawer === 'function') {
              window.nsicdGarage.toggleDrawer();
            } else {
              // Fallback: manually open the drawer
              const garageDrawer = document.querySelector('.nsicd-garage-drawer, [data-garage-drawer]');
              if (garageDrawer) {
                garageDrawer.classList.add('open');
                document.body.classList.add('garage-drawer-open');
              }
            }
          } else {
            this.saveToGarage();
          }
        }
      });
    }

    async loadFitments(yearId) {
      const loadingEl = this.querySelector('[data-nsicd-loading]');
      const contentEl = this.querySelector('[data-nsicd-content]');

      if (loadingEl) loadingEl.style.display = 'block';
      if (contentEl) contentEl.style.display = 'none';

      try {
        const response = await fetch(`${NSICD_CONFIG.apiBase}?action=get_fitments&year_id=${yearId}`, {
          credentials: 'include'
        });
        const data = await response.json();

        if (data.success) {
          this.vehicle = data.vehicle;
          this.fitments = data.fitments;
          this.render();

          // Auto-save to garage when viewing results
          this.autoSaveToGarage();
        } else {
          console.error('API returned error:', data.error);
          if (loadingEl) {
            loadingEl.innerHTML = `<div style="text-align: center; padding: 3rem; color: #dc2626;">
              <p>Error loading vehicle data. Please try again.</p>
              <a href="/pages/vehicle-fitment" class="nsicd-btn" style="margin-top: 1rem;">Search Again</a>
            </div>`;
          }
          return;
        }
      } catch (error) {
        console.error('Error loading fitments:', error);
        if (loadingEl) {
          loadingEl.innerHTML = `<div style="text-align: center; padding: 3rem; color: #dc2626;">
            <p>Error connecting to server. Please try again.</p>
            <a href="/pages/vehicle-fitment" class="nsicd-btn" style="margin-top: 1rem;">Search Again</a>
          </div>`;
        }
        return;
      }

      if (loadingEl) loadingEl.style.display = 'none';
      if (contentEl) contentEl.style.display = 'block';
    }

    render() {
      const contentEl = this.querySelector('[data-nsicd-content]');
      if (!contentEl || !this.vehicle) return;

      // Update vehicle title
      const titleEl = this.querySelector('[data-vehicle-title]');
      if (titleEl) {
        let title = `${this.vehicle.year} ${this.vehicle.make_name} ${this.vehicle.model_name}`;
        if (this.vehicle.style) {
          title += ` ${this.vehicle.style}`;
        }
        titleEl.textContent = title;
      }

      // Render fitments grid
      const gridEl = this.querySelector('[data-fitments-grid]');
      if (gridEl) {
        const validFitments = this.fitments.filter(f => f.size_name && f.size_name !== 'Nothing');

        if (validFitments.length === 0) {
          // No fitments found for this vehicle
          gridEl.innerHTML = `
            <div class="nsicd-results__no-fitments">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="48" height="48">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" />
              </svg>
              <h3>No Speaker Data Yet</h3>
              <p>We're still gathering speaker fitment information for this vehicle.</p>
              <p>Browse our <a href="/collections/speaker">speaker collection</a> to find compatible options.</p>
            </div>
          `;
        } else {
          gridEl.innerHTML = validFitments.map(f => this.renderFitmentCard(f)).join('');
        }
      }

      // Show save button if not already in garage
      this.checkIfInGarage();
    }

    renderFitmentCard(fitment) {
      // Build search URL - link to collection page with tag filter
      // This uses native Shopify collection filtering for proper facets/sorting
      const sizeTag = fitment.shopify_tag || '';

      // Link to speaker collection with tag filter for facets/sorting
      // Format: /collections/speaker/TAG?year_id=XXXX
      // Include year_id so filters know which vehicle context to use
      const yearId = this.vehicle ? this.vehicle.year_id : '';
      const collectionUrl = `/collections/speaker/${encodeURIComponent(sizeTag)}${yearId ? `?year_id=${yearId}` : ''}`;

      return `
        <div class="nsicd-fitment-card">
          <div class="nsicd-fitment-card__location">${fitment.location_name}</div>
          <a href="${collectionUrl}" class="nsicd-fitment-card__size">
            ${fitment.size_name}
          </a>
        </div>
      `;
    }

    async checkIfInGarage() {
      const saveBtn = this.querySelector('[data-save-to-garage]');
      if (!saveBtn || !this.vehicle) return;

      // Check localStorage first
      const garage = JSON.parse(localStorage.getItem(NSICD_CONFIG.storageKeys.garage) || '[]');
      const isInGarage = garage.some(v => v.year_id == this.vehicle.year_id);

      if (isInGarage) {
        this.updateButtonToSaved(saveBtn);
      }
    }

    updateButtonToSaved(saveBtn) {
      // Update button to show "In Your Garage" but keep it clickable
      const textEl = saveBtn.querySelector('[data-btn-text]');
      const plusIcon = saveBtn.querySelector('[data-icon="plus"]');
      const garageIcon = saveBtn.querySelector('[data-icon="garage"]');

      if (textEl) {
        textEl.textContent = 'In Your Garage';
      } else {
        saveBtn.innerHTML = 'In Your Garage';
      }

      // Swap icons if they exist
      if (plusIcon) plusIcon.style.display = 'none';
      if (garageIcon) garageIcon.style.display = 'inline';

      saveBtn.classList.add('saved');
      saveBtn.dataset.inGarage = 'true';
    }

    async autoSaveToGarage() {
      if (!this.vehicle) return;

      // Check if already in garage
      let garage = JSON.parse(localStorage.getItem(NSICD_CONFIG.storageKeys.garage) || '[]');
      const existingVehicle = garage.find(v => v.year_id == this.vehicle.year_id);

      if (existingVehicle) {
        // Already saved - set this vehicle as primary in localStorage
        garage = garage.map(v => ({
          ...v,
          is_primary: v.year_id == this.vehicle.year_id ? 1 : 0
        }));
        localStorage.setItem(NSICD_CONFIG.storageKeys.garage, JSON.stringify(garage));

        // Update button
        const saveBtn = this.querySelector('[data-save-to-garage]');
        if (saveBtn) {
          this.updateButtonToSaved(saveBtn);
        }
        return;
      }

      // Auto-save to garage
      let nickname = `${this.vehicle.year} ${this.vehicle.make_name} ${this.vehicle.model_name}`;
      if (this.vehicle.style) {
        nickname += ` ${this.vehicle.style}`;
      }

      const vehicleData = {
        year_id: this.vehicle.year_id,
        year: this.vehicle.year,
        make: this.vehicle.make_name,
        make_name: this.vehicle.make_name,
        model: this.vehicle.model_name,
        model_name: this.vehicle.model_name,
        style: this.vehicle.style || null,
        nickname: nickname,
        is_primary: 1
      };

      // Save to localStorage FIRST (so filters work immediately)
      garage = garage.map(v => ({ ...v, is_primary: 0 }));
      garage.push({ ...vehicleData, garage_id: 'pending_' + Date.now() });
      localStorage.setItem(NSICD_CONFIG.storageKeys.garage, JSON.stringify(garage));

      // Update button immediately
      const saveBtn = this.querySelector('[data-save-to-garage]');
      if (saveBtn) {
        this.updateButtonToSaved(saveBtn);
      }

      // Dispatch event for garage icon update
      window.dispatchEvent(new CustomEvent('nsicd-garage-updated'));

      // Then sync with API in background
      try {
        const response = await fetch(`${NSICD_CONFIG.apiBase}?action=save_vehicle`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            ...vehicleData,
            shopify_customer_id: this.customerId
          })
        });

        const data = await response.json();

        if (data.success && data.garage_id) {
          // Update the garage_id from pending to actual
          garage = JSON.parse(localStorage.getItem(NSICD_CONFIG.storageKeys.garage) || '[]');
          garage = garage.map(v => {
            if (v.year_id == this.vehicle.year_id && String(v.garage_id).startsWith('pending_')) {
              return { ...v, garage_id: data.garage_id };
            }
            return v;
          });
          localStorage.setItem(NSICD_CONFIG.storageKeys.garage, JSON.stringify(garage));
        }
      } catch (error) {
        console.error('API sync failed (vehicle saved locally):', error);
      }
    }

    async saveToGarage() {
      if (!this.vehicle) return;

      const saveBtn = this.querySelector('[data-save-to-garage]');
      if (saveBtn) {
        saveBtn.disabled = true;
        saveBtn.innerHTML = '<span class="loading-spinner"></span> Saving...';
      }

      let nickname = `${this.vehicle.year} ${this.vehicle.make_name} ${this.vehicle.model_name}`;
      if (this.vehicle.style) {
        nickname += ` ${this.vehicle.style}`;
      }

      const vehicleData = {
        year_id: this.vehicle.year_id,
        year: this.vehicle.year,
        make: this.vehicle.make_name,
        model: this.vehicle.model_name,
        style: this.vehicle.style || null,
        nickname: nickname
      };

      try {
        // Save to API
        const response = await fetch(`${NSICD_CONFIG.apiBase}?action=save_vehicle`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            ...vehicleData,
            shopify_customer_id: this.customerId
          })
        });

        const data = await response.json();

        if (data.success) {
          // Also save to localStorage
          const garage = JSON.parse(localStorage.getItem(NSICD_CONFIG.storageKeys.garage) || '[]');
          garage.push({ ...vehicleData, garage_id: data.garage_id });
          localStorage.setItem(NSICD_CONFIG.storageKeys.garage, JSON.stringify(garage));

          // Update UI
          if (saveBtn) {
            saveBtn.innerHTML = '<i class="icon-check"></i> Saved to Garage!';
            saveBtn.classList.add('saved');
          }

          // Dispatch event for garage icon update
          window.dispatchEvent(new CustomEvent('nsicd-garage-updated'));
        } else {
          throw new Error(data.error || 'Failed to save');
        }
      } catch (error) {
        console.error('Error saving to garage:', error);
        if (saveBtn) {
          saveBtn.disabled = false;
          saveBtn.innerHTML = 'Save to My Garage';
        }
        alert('Failed to save vehicle. Please try again.');
      }
    }
  }

  if (!customElements.get('nsicd-results')) {
    customElements.define('nsicd-results', NsicdResults);
  }

  // ============================================================
  // Garage Manager
  // ============================================================

  class NsicdGarage {
    constructor() {
      this.garage = [];
      this.activeVehicle = null;
      this.customerId = null;
      this.drawerEl = null;
      this.init();
    }

    init() {
      // Get customer ID
      const customerMeta = document.querySelector('meta[name="shopify-customer-id"]');
      if (customerMeta && customerMeta.content) {
        this.customerId = customerMeta.content;
      }

      // Get drawer element
      this.drawerEl = document.querySelector('[data-garage-drawer]');

      // Load garage
      this.loadGarage();

      // Set up event listeners
      this.setupEventListeners();
    }

    setupEventListeners() {
      // Use event delegation on document for garage toggle - more robust
      document.addEventListener('click', (e) => {
        // Handle garage toggle button click
        const toggleBtn = e.target.closest('[data-garage-toggle]');
        if (toggleBtn) {
          e.preventDefault();
          e.stopPropagation();
          this.toggleDrawer();
          return;
        }

        // Handle close button click
        const closeBtn = e.target.closest('[data-garage-close]');
        if (closeBtn) {
          e.preventDefault();
          this.closeDrawer();
          return;
        }

        // Close drawer when clicking outside
        if (this.drawerEl &&
            this.drawerEl.classList.contains('open') &&
            !this.drawerEl.contains(e.target)) {
          this.closeDrawer();
        }
      });

      // Listen for garage updates
      window.addEventListener('nsicd-garage-updated', () => this.loadGarage());

      // Handle drawer actions via delegation
      if (this.drawerEl) {
        this.drawerEl.addEventListener('click', (e) => {
          const removeBtn = e.target.closest('[data-remove-vehicle]');
          const setPrimaryBtn = e.target.closest('[data-set-primary]');
          const selectBtn = e.target.closest('[data-select-vehicle]');

          if (removeBtn) {
            this.removeVehicle(removeBtn.dataset.garageId);
          } else if (setPrimaryBtn) {
            this.setPrimary(setPrimaryBtn.dataset.garageId);
          } else if (selectBtn) {
            this.selectVehicle(selectBtn.dataset.yearId);
          }
        });
      }
    }

    async loadGarage() {
      // Load from localStorage first for instant display
      const localGarage = JSON.parse(localStorage.getItem(NSICD_CONFIG.storageKeys.garage) || '[]');
      this.garage = localGarage;
      this.updateBadge();
      this.updateDrawer();

      // Then sync with API
      try {
        const url = new URL(NSICD_CONFIG.apiBase);
        url.searchParams.set('action', 'get_garage');
        if (this.customerId) {
          url.searchParams.set('shopify_customer_id', this.customerId);
        }

        const response = await fetch(url.toString(), { credentials: 'include' });
        const data = await response.json();

        if (data.success) {
          const apiVehicles = data.vehicles || [];

          // Merge: keep local vehicles that aren't in API, add API vehicles
          // This preserves locally-saved vehicles that haven't synced yet
          const mergedGarage = [...localGarage];

          apiVehicles.forEach(apiVehicle => {
            const existsLocally = mergedGarage.some(v => v.year_id == apiVehicle.year_id);
            if (!existsLocally) {
              mergedGarage.push(apiVehicle);
            } else {
              // Update local with API data (e.g., get real garage_id)
              const idx = mergedGarage.findIndex(v => v.year_id == apiVehicle.year_id);
              if (idx !== -1) {
                mergedGarage[idx] = { ...mergedGarage[idx], ...apiVehicle };
              }
            }
          });

          this.garage = mergedGarage;
          localStorage.setItem(NSICD_CONFIG.storageKeys.garage, JSON.stringify(this.garage));
          this.updateBadge();
          this.updateDrawer();

          // Set active vehicle
          if (this.garage.length > 0) {
            const primary = this.garage.find(v => v.is_primary) || this.garage[0];
            this.setActiveVehicle(primary);
          }
        }
      } catch (error) {
        console.error('Error loading garage:', error);
      }
    }

    setActiveVehicle(vehicle) {
      this.activeVehicle = vehicle;
      localStorage.setItem(NSICD_CONFIG.storageKeys.activeVehicle, JSON.stringify(vehicle));
      window.dispatchEvent(new CustomEvent('nsicd-active-vehicle-changed', { detail: vehicle }));
    }

    updateBadge() {
      const badges = document.querySelectorAll('[data-garage-count]');
      badges.forEach(badge => {
        const count = this.garage.length;
        badge.textContent = count;
        badge.style.display = count > 0 ? 'flex' : 'none';
      });
    }

    updateDrawer() {
      const vehiclesList = document.querySelector('[data-garage-vehicles]');
      if (!vehiclesList) return;

      if (this.garage.length === 0) {
        vehiclesList.innerHTML = `
          <div class="nsicd-garage-empty">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="48" height="48">
              <path d="M3 10L12 3L21 10" />
              <path d="M5 10V20H19V10" />
              <rect x="7" y="12" width="10" height="8" rx="0.5" />
              <line x1="7" y1="14.5" x2="17" y2="14.5" />
              <line x1="7" y1="17" x2="17" y2="17" />
            </svg>
            <p>No vehicles saved yet</p>
            <a href="/pages/vehicle-fitment" class="nsicd-btn">Find Your Vehicle</a>
          </div>
        `;
        return;
      }

      vehiclesList.innerHTML = this.garage.map(vehicle => {
        let vehicleName = `${vehicle.year} ${vehicle.make_name} ${vehicle.model_name}`;
        if (vehicle.style) {
          vehicleName += ` ${vehicle.style}`;
        }
        return `
        <div class="nsicd-garage-vehicle ${vehicle.is_primary ? 'is-primary' : ''}">
          <div class="nsicd-garage-vehicle__info">
            <div class="nsicd-garage-vehicle__name">
              ${vehicleName}
            </div>
            ${vehicle.is_primary ? '<span class="nsicd-garage-vehicle__badge">Primary</span>' : ''}
          </div>
          <div class="nsicd-garage-vehicle__actions">
            <button type="button" class="nsicd-garage-btn" data-select-vehicle data-year-id="${vehicle.year_id}" title="View Fitments">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="18" height="18">
                <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
            ${!vehicle.is_primary ? `
              <button type="button" class="nsicd-garage-btn" data-set-primary data-garage-id="${vehicle.garage_id}" title="Set as Primary">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="18" height="18">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                </svg>
              </button>
            ` : ''}
            <button type="button" class="nsicd-garage-btn nsicd-garage-btn--danger" data-remove-vehicle data-garage-id="${vehicle.garage_id}" title="Remove">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="18" height="18">
                <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
            </button>
          </div>
        </div>
      `}).join('');
    }

    toggleDrawer() {
      if (this.drawerEl) {
        this.drawerEl.classList.toggle('open');
        document.body.classList.toggle('garage-drawer-open');
      }
    }

    closeDrawer() {
      if (this.drawerEl) {
        this.drawerEl.classList.remove('open');
        document.body.classList.remove('garage-drawer-open');
      }
    }

    async removeVehicle(garageId) {
      if (!confirm('Remove this vehicle from your garage?')) return;

      // Check if this is a "pending" vehicle (not yet synced to API)
      const isPending = String(garageId).startsWith('pending_');

      if (isPending) {
        // Just remove from localStorage - no API call needed
        this.garage = this.garage.filter(v => v.garage_id != garageId);
        localStorage.setItem(NSICD_CONFIG.storageKeys.garage, JSON.stringify(this.garage));
        this.updateBadge();
        this.updateDrawer();
        return;
      }

      try {
        const response = await fetch(`${NSICD_CONFIG.apiBase}?action=remove_vehicle`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            garage_id: garageId,
            shopify_customer_id: this.customerId
          })
        });

        const data = await response.json();

        if (data.success) {
          // Update localStorage
          this.garage = this.garage.filter(v => v.garage_id != garageId);
          localStorage.setItem(NSICD_CONFIG.storageKeys.garage, JSON.stringify(this.garage));
          this.updateBadge();
          this.updateDrawer();
        } else {
          // API failed but still remove from localStorage
          console.warn('API remove failed, removing from localStorage only:', data.error);
          this.garage = this.garage.filter(v => v.garage_id != garageId);
          localStorage.setItem(NSICD_CONFIG.storageKeys.garage, JSON.stringify(this.garage));
          this.updateBadge();
          this.updateDrawer();
        }
      } catch (error) {
        console.error('Error removing vehicle:', error);
        // Still remove from localStorage on error
        this.garage = this.garage.filter(v => v.garage_id != garageId);
        localStorage.setItem(NSICD_CONFIG.storageKeys.garage, JSON.stringify(this.garage));
        this.updateBadge();
        this.updateDrawer();
      }
    }

    async setPrimary(garageId) {
      try {
        const response = await fetch(`${NSICD_CONFIG.apiBase}?action=set_primary`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            garage_id: garageId,
            shopify_customer_id: this.customerId
          })
        });

        const data = await response.json();

        if (data.success) {
          // Update localStorage
          this.garage.forEach(v => {
            v.is_primary = v.garage_id == garageId ? 1 : 0;
          });
          localStorage.setItem(NSICD_CONFIG.storageKeys.garage, JSON.stringify(this.garage));
          this.updateDrawer();

          const primary = this.garage.find(v => v.is_primary);
          if (primary) {
            this.setActiveVehicle(primary);
          }
        }
      } catch (error) {
        console.error('Error setting primary:', error);
      }
    }

    selectVehicle(yearId) {
      this.closeDrawer();
      // Find vehicle info from garage
      const vehicle = this.garage.find(v => v.year_id == yearId);
      if (vehicle) {
        const urlParams = [
          `year_id/${yearId}`,
          `Year/${vehicle.year}`,
          `Make/${encodeURIComponent(vehicle.make_name || vehicle.make)}`,
          `Model/${encodeURIComponent(vehicle.model_name || vehicle.model)}`
        ];
        if (vehicle.style) {
          urlParams.push(`Style/${encodeURIComponent(vehicle.style)}`);
        }
        window.location.href = `/pages/vehicle-results?${urlParams.join('/')}`;
      } else {
        window.location.href = `/pages/search?year_id/${yearId}`;
      }
    }
  }

  // ============================================================
  // Initialize on DOM Ready
  // ============================================================

  function initNSICD() {
    // Initialize garage manager
    if (document.querySelector('[data-garage-drawer]') || document.querySelector('[data-garage-toggle]')) {
      window.nsicdGarage = new NsicdGarage();
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initNSICD);
  } else {
    initNSICD();
  }

})();
